import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, ExternalLink, Globe, User, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Media = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const [postsResponse, mentionsResponse] = await Promise.all([
          axios.get(`${API}/blog-posts`),
          axios.get(`${API}/media-monitor`),
        ]);
        setBlogPosts(postsResponse.data);
        setMentions(mentionsResponse.data);
      } catch (error) {
        console.error('Error fetching media content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      Leadership: '#53a5ca',
      'Mental Health': '#e37c55',
      Events: '#dbbf57',
      Community: '#7e2f22',
    };
    return colors[category] || '#53a5ca';
  };

  return (
    <div className="min-h-screen">
      <section className="py-16 md:py-24" style={{ backgroundColor: '#dbbf5720' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Media & Blog
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Stories, insights, and updates from NAIWA Daughters, plus recent web mentions and social momentum around our work.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
          <div className="space-y-4">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#53a5ca20', color: '#53a5ca' }}>
              Most recent ND social media post
            </div>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#7e2f22' }}>Latest social momentum</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              This section keeps the most recent NAIWA Daughters social post visible alongside our stories and external mentions.
            </p>
          </div>

          <div className="rounded-[28px] overflow-hidden shadow-xl bg-white p-2" data-testid="media-latest-social-post">
            <iframe
              src="https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fnaiwadaughterscherokee%2Fposts%2Fpfbid02JrjDBy6yzSgVDZ6ESBCvhMQJkBhaXbjXxB3XfQ2hWXZTcnyDqaEs5VNkATkQdJbNl&show_text=true&width=500"
              width="100%"
              height="540"
              style={{ border: 'none', overflow: 'hidden' }}
              scrolling="no"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              title="Most recent NAIWA Daughters social media post"
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Recent web mentions</h2>
              <p className="text-lg text-gray-600">Public mentions auto-collected from a Google News-style monitor for NAIWA Daughters.</p>
            </div>
          </div>

          {mentions.length ? (
            <div className="grid md:grid-cols-2 gap-6">
              {mentions.map((mention, index) => (
                <Card key={`${mention.link}-${index}`} className="border-0 shadow-lg" data-testid={`media-mention-card-${index}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <Badge style={{ backgroundColor: '#53a5ca', color: 'white' }}>Google Monitor</Badge>
                      <span className="text-xs text-gray-500">{formatDate(mention.publishedAt)}</span>
                    </div>
                    <CardTitle className="text-xl" style={{ color: '#7e2f22' }}>{mention.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Globe className="h-4 w-4" />
                      <span>{mention.source}</span>
                    </div>
                    <p className="text-gray-600 line-clamp-3">{mention.summary}</p>
                    <a href={mention.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold" style={{ color: '#53a5ca' }} data-testid={`media-mention-link-${index}`}>
                      Read mention <ExternalLink className="h-4 w-4" />
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : loading ? <p className="text-gray-500">Loading recent mentions...</p> : <p className="text-gray-500">No recent mentions found yet.</p>}
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading blog posts...</p>
            </div>
          ) : blogPosts.length > 0 ? (
            <div className="max-w-5xl mx-auto">
              <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative h-64 md:h-auto overflow-hidden">
                    <img src={blogPosts[0].image} alt={blogPosts[0].title} className="w-full h-full object-cover" />
                    <div className="absolute top-4 left-4">
                      <Badge className="text-xs font-semibold" style={{ backgroundColor: getCategoryColor(blogPosts[0].category), color: 'white' }}>
                        {blogPosts[0].category}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-8">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#53a5ca20', color: '#53a5ca' }}>
                        Featured Post
                      </span>
                    </div>
                    <CardTitle className="text-3xl mb-4" style={{ color: '#7e2f22' }}>{blogPosts[0].title}</CardTitle>
                    <p className="text-gray-600 mb-6 leading-relaxed">{blogPosts[0].excerpt}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
                      <div className="flex items-center space-x-1"><User className="h-4 w-4" /><span>{blogPosts[0].author}</span></div>
                      <div className="flex items-center space-x-1"><Calendar className="h-4 w-4" /><span>{formatDate(blogPosts[0].date)}</span></div>
                    </div>
                    <Link to={blogPosts[0].externalLink ? blogPosts[0].externalLink : `/media/${blogPosts[0].id}`} target={blogPosts[0].externalLink ? '_blank' : '_self'} rel={blogPosts[0].externalLink ? 'noopener noreferrer' : ''}>
                      <button className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:opacity-90 flex items-center space-x-2 text-white" style={{ backgroundColor: '#53a5ca' }}>
                        <span>Read Full Story</span>
                        <ArrowRight className="h-5 w-5" />
                      </button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No blog posts available</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {!loading && blogPosts.length > 1 && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: '#7e2f22' }}>Recent Stories</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.slice(1).map((post) => (
                  <Card key={post.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                    <div className="relative h-48 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <Badge className="text-xs font-semibold" style={{ backgroundColor: getCategoryColor(post.category), color: 'white' }}>{post.category}</Badge>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl line-clamp-2" style={{ color: '#7e2f22' }}>{post.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                        <div className="flex items-center space-x-1"><User className="h-3 w-3" /><span>{post.author}</span></div>
                        <div className="flex items-center space-x-1"><Calendar className="h-3 w-3" /><span>{formatDate(post.date)}</span></div>
                      </div>
                      <Link to={post.externalLink ? post.externalLink : `/media/${post.id}`} target={post.externalLink ? '_blank' : '_self'} rel={post.externalLink ? 'noopener noreferrer' : ''}>
                        <button className="w-full py-2 rounded-lg font-semibold text-sm transition-all duration-200 hover:opacity-90 flex items-center justify-center space-x-2" style={{ backgroundColor: '#e37c5520', color: '#e37c55' }}>
                          <span>Read More</span>
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Media;
